import { Injectable } from '@angular/core';

import tinycolor from 'tinycolor2';

export interface Color {
  name: string;
  hex: string;
  darkContrast: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class CaDynamicThemeService {
  caOrange = '#f15a0e';
  caOrangeLight = '#ffdac9';
  caGreen = '#4fb1a6';
  caGreenDark = '#3a827a';
  caGreenLight = '#d8f2ee;';
  caBrandFabBackground = '#f15a0e';
  caBrandFabForeground = '#ffd43c';
  caBrandFabShadow: '#e25e19';
  caBrandSuccess = '#37b24d';
  caBrandWarning = '#fab005';
  caBrandFailure = '#f03e3e';
  caBrandBackground = '#ffffff';
  caBrandControl = '#212529';
  caBrandText = '#212529';

  /**
   * Applies a colour scheme based on the Organisation Branding colour.
   *
   * @param primaryColor The main branding colour for the organisation.
   * @param fabForeground The FAB foreground colour used for text and shadow.
   * @param fabBackground The FAB background colour used for shading.
   */
  applyThemeFromOrganisationBranding(
    primaryColor: string,
    fabForeground: string,
    fabBackground: string,
    fabShadow?: string
  ) {
    this.savePrimaryTheme(primaryColor);
    this.saveAccentTheme(primaryColor);

    const primaryLightHsl = tinycolor(primaryColor).toHsl();
    primaryLightHsl.l = 0.9;
    const primaryLight = tinycolor(primaryLightHsl);

    document.documentElement.style.setProperty('--ca-primary-action', primaryColor);
    document.documentElement.style.setProperty(
      '--ca-primary-action-light',
      tinycolor(primaryLight).toHexString()
    );
    document.documentElement.style.setProperty('--ca-info', primaryColor);
    document.documentElement.style.setProperty('--ca-info-light', primaryLight.toHexString());
    document.documentElement.style.setProperty('--ca-fab-foreground', fabForeground);
    document.documentElement.style.setProperty('--ca-fab-background', fabBackground);
    if (fabShadow) {
      document.documentElement.style.setProperty('--ca-fab-shadow', fabShadow);
    } else {
      document.documentElement.style.setProperty('--ca-fab-shadow', fabForeground);
    }
    document.documentElement.style.setProperty('--ca-drawer-action', '--var(--ca-text-color)');
  }

  /**
   * Applies the CareApp brand colour scheme to the app.
   */
  applyCareAppTheme() {
    this.savePrimaryTheme(this.caOrange);
    this.saveAccentTheme(this.caGreen);

    document.documentElement.style.setProperty('--ca-primary-action', 'var(--ca-orange)');
    document.documentElement.style.setProperty(
      '--ca-primary-action-light',
      'var(--ca-orange-light)'
    );
    document.documentElement.style.setProperty('--ca-info', 'var(--ca-green)');
    document.documentElement.style.setProperty('--ca-info-light', 'var(--ca-green-light)');
    document.documentElement.style.setProperty(
      '--ca-fab-foreground',
      'var(--ca-brand-fab-foreground)'
    );
    document.documentElement.style.setProperty(
      '--ca-fab-background',
      'var(--ca-brand-fab-background)'
    );
    document.documentElement.style.setProperty('--ca-fab-shadow', 'var(--ca-brand-fab-shadow)');
    document.documentElement.style.setProperty('--ca-drawer-action', 'var(--ca-orange)');
    document.documentElement.style.setProperty('--ca-control', 'var(--ca-brand-control)');
  }

  /**
   * Updates the primary theme.
   *
   * @param color The base colour of the palette
   */
  savePrimaryTheme(color: string) {
    const palette = this.computePalette(color);
    this.updateTheme(palette, 'primary');
  }

  /**
   * Updates the warning theme.
   *
   * @param color The base colour of the palette
   */
  saveWarnTheme(color: string) {
    const palette = this.computePalette(color);
    this.updateTheme(palette, 'warn');
  }

  /**
   * Updates the accent theme.
   *
   * @param color The base colour of the palette
   */
  saveAccentTheme(color: string) {
    const palette = this.computePalette(color);
    this.updateTheme(palette, 'accent');
  }

  /**
   * Updates the css variables defined in `palette` for a particular Material
   * colour palette.
   *
   * @param colors The array of colours representing the theme
   * @param themeName The name of this theme (primary, accent, etc)
   */
  private updateTheme(colors: Color[], themeName: string) {
    for (let color of colors) {
      document.documentElement.style.setProperty(`--theme-${themeName}-${color.name}`, color.hex);
      document.documentElement.style.setProperty(
        `--theme-${themeName}-contrast-${color.name}`,
        color.darkContrast ? 'rgba(black, 0.87)' : 'white'
      );
    }
  }

  /**
   * Calculates a Material colour palette for a base colour.
   *
   * @param hex The base colour in hex format for this palette
   * @returns The material colour palette as an array of Color
   */
  private computePalette(hex: string): Color[] {
    return [
      this.getColorObject(tinycolor(hex).lighten(52), '50'),
      this.getColorObject(tinycolor(hex).lighten(37), '100'),
      this.getColorObject(tinycolor(hex).lighten(26), '200'),
      this.getColorObject(tinycolor(hex).lighten(12), '300'),
      this.getColorObject(tinycolor(hex).lighten(6), '400'),
      this.getColorObject(tinycolor(hex), '500'),
      this.getColorObject(tinycolor(hex).darken(6), '600'),
      this.getColorObject(tinycolor(hex).darken(12), '700'),
      this.getColorObject(tinycolor(hex).darken(18), '800'),
      this.getColorObject(tinycolor(hex).darken(24), '900'),
      this.getColorObject(tinycolor(hex).lighten(50).saturate(30), 'A100'),
      this.getColorObject(tinycolor(hex).lighten(30).saturate(30), 'A200'),
      this.getColorObject(tinycolor(hex).lighten(10).saturate(15), 'A400'),
      this.getColorObject(tinycolor(hex).lighten(5).saturate(5), 'A700'),
    ];
  }

  /**
   * Convenience method for creating a colour object.
   *
   * @param value The colour in hex format
   * @param name The name of the colour
   * @returns a Color
   */
  private getColorObject(value, name): Color {
    const c = tinycolor(value);
    return {
      name: name,
      hex: c.toHexString(),
      darkContrast: c.isLight(),
    };
  }
}
